import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    tracify: any;
    Tracify: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TracifyService {
  private isInitialized = false;
  private lastProductViewTime = 0;
  private lastPageViewTime = 0;
  private readonly DEBOUNCE_DELAY = 3000; // 3 second debounce for more aggressive filtering
  private isTrackingProduct = false; // Prevent concurrent product tracking
  private lastTrackedProductId: string | null = null; // Track last product to avoid duplicates

  constructor(private router: Router) {}

  /**
   * Load Tracify script dynamically
   */
  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.tracify) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = environment.tracify.scriptUrl;
      script.async = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Tracify script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Tracify with configuration
   */
  async initializeTracifyScript(): Promise<void> {
    try {
      await this.loadScript();

      if (typeof window !== 'undefined' && typeof window.Tracify !== 'undefined') {
        window.tracify = window.Tracify({
          csid: environment.tracify.csid,
          fingerPrint: true
        });
        this.isInitialized = true;
      }
    } catch (error) {
      console.warn('Tracify initialization failed:', error);
    }
  }

  private initializeTracify(): void {
    if (this.isInitialized || typeof window === 'undefined' || !window.tracify) {
      return;
    }

    // Check if Tracify is properly loaded
    try {
      if (typeof window.tracify?.capturePageView === 'function') {
        this.isInitialized = true;
      }
    } catch (error) {
      // console.warn('Tracify library not properly loaded:', error);
    }
  }

  /**
   * Track page views automatically when router navigates
   */
  trackPageView(): void {
    const now = Date.now();

    // Debounce: Prevent duplicate calls within 1 second
    if (now - this.lastPageViewTime < this.DEBOUNCE_DELAY) {
      // console.log('[TRACIFY] trackPageView call debounced (too soon)');
      return;
    }

    const timeSinceLastCall = now - this.lastPageViewTime;
    this.lastPageViewTime = now;
    // console.log('[TRACIFY] trackPageView called (allowed)');
    // console.log(`[TRACIFY] Time since last page view: ${timeSinceLastCall}ms`);
    this.initializeTracify();
    if (window.tracify && window.tracify.capturePageView) {
      try {
        // console.log('[TRACIFY] Capturing page view');
        window.tracify.capturePageView();
        // console.log('[TRACIFY] Page view captured successfully');
      } catch (error) {
        // console.error('[TRACIFY] Page view tracking error:', error);
      }
    } else {
      // console.warn('[TRACIFY] Tracify not available or capturePageView method missing');
    }
  }

  /**
   * Track product views
   */
  trackProductView(productId?: string): void {
    const now = Date.now();

    // Prevent concurrent tracking
    if (this.isTrackingProduct) {
      return;
    }

    // Prevent tracking the same product multiple times
    if (productId && this.lastTrackedProductId === productId) {
      return;
    }

    // Debounce: Prevent duplicate calls within 3 seconds
    if (now - this.lastProductViewTime < this.DEBOUNCE_DELAY) {
      // console.log('[TRACIFY] trackProductView call debounced (too soon)');
      return;
    }

    this.lastProductViewTime = now;
    this.isTrackingProduct = true;
    if (productId) {
      this.lastTrackedProductId = productId;
    }
    // console.log('[TRACIFY] trackProductView called (allowed)');
    this.initializeTracify();
    if (window.tracify && window.tracify.captureProductView) {
      try {
        // console.log('[TRACIFY] Capturing product view');
        window.tracify.captureProductView();
        // console.log('[TRACIFY] Product view captured successfully');
      } catch (error) {
        // console.error('[TRACIFY] Product view tracking error:', error);
      }
    } else {
      // console.warn('[TRACIFY] Tracify not available or captureProductView method missing');
    }

    // Reset tracking flag after a short delay to prevent rapid successive calls
    setTimeout(() => {
      this.isTrackingProduct = false;
    }, 500);
  }

  /**
   * Track add to cart events
   */
  trackAddToCart(): void {
    // console.log('[TRACIFY] trackAddToCart called');
    this.initializeTracify();
    if (window.tracify && window.tracify.captureAddToCart) {
      try {
        // console.log('[TRACIFY] Capturing add to cart');
        window.tracify.captureAddToCart();
        // console.log('[TRACIFY] Add to cart captured successfully');
      } catch (error) {
        // console.error('[TRACIFY] Add to cart tracking error:', error);
      }
    } else {
      // console.warn('[TRACIFY] Tracify not available or captureAddToCart method missing');
    }
  }

  /**
   * Track purchase events
   */
  trackPurchase(data: {
    amount: string | number;
    orderId: string;
    currency?: string;
    email?: string;
    isReturningCustomer?: boolean;
    orderIdIsSensitive?: boolean;
    discountCodes?: string[];
  }): void {
    // console.log('[TRACIFY] trackPurchase called with data:', data);
    this.initializeTracify();
    if (window.tracify && window.tracify.capturePurchase) {
      try {
        // console.log('[TRACIFY] Capturing purchase');
        window.tracify.capturePurchase({
          amount: typeof data.amount === 'number' ? data.amount.toString() : data.amount,
          orderId: data.orderId,
          currency: data.currency || 'EUR',
          email: data.email,
          isReturningCustomer: data.isReturningCustomer || false,
          orderIdIsSensitive: data.orderIdIsSensitive || false,
          discountCodes: data.discountCodes || []
        });
        // console.log('[TRACIFY] Purchase captured successfully');
      } catch (error) {
        // console.error('[TRACIFY] Purchase tracking error:', error);
      }
    } else {
      // console.warn('[TRACIFY] Tracify not available or capturePurchase method missing');
    }
  }

  /**
   * Track conversion events (leads, signups, etc.)
   */
  trackConversion(data: {
    value: number;
    conversionId?: string;
    currency?: string;
  }): void {
    this.initializeTracify();
    if (window.tracify && window.tracify.captureConversion) {
      try {
        window.tracify.captureConversion({
          value: data.value,
          conversionId: data.conversionId,
          currency: data.currency || 'EUR'
        });
      } catch (error) {
        // console.error('Tracify conversion tracking error:', error);
      }
    }
  }

  /**
   * Track custom events
   */
  trackCustomEvent(eventName: string, data?: any): void {
    this.initializeTracify();
    if (window.tracify && window.tracify.capture) {
      try {
        window.tracify.capture(eventName, data);
      } catch (error) {
        // console.error('Tracify custom event tracking error:', error);
      }
    }
  }

  /**
   * Setup router event listener for automatic page view tracking
   */
  setupPageViewTracking(): void {
    // console.log('[TRACIFY] Setting up page view tracking');
    this.router.events.subscribe(event => {
      // Only track NavigationEnd events (when navigation completes)
      if (event instanceof NavigationEnd) {
        // console.log('[TRACIFY] Navigation completed, tracking page view for:', event.urlAfterRedirects);
        // Use setTimeout to ensure the page has fully loaded
        setTimeout(() => {
          this.trackPageView();
        }, 100);
      }
    });
  }
}